"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { getTemplateStyles } from "@/lib/siteTemplateStyles";
import { GalleryLightbox } from "@/components/gallery/GalleryLightbox";

type GalleryImage = {
  url?: string;
  altText?: string;
  caption?: string;
};

export function GalleryCarouselBlock({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const heading = String(content.heading ?? "");
  const intro = String(content.intro ?? "");
  const images = (Array.isArray(content.images) ? content.images : []) as GalleryImage[];
  const lightboxImages = images.filter((image): image is GalleryImage & { url: string } => Boolean(image.url));
  const autoplay = Boolean(content.autoplay);
  const styles = getTemplateStyles(template);
  const [active, setActive] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const go = useCallback(
    (index: number) => {
      const next = Math.max(0, Math.min(index, images.length - 1));
      setActive(next);
    },
    [images.length],
  );

  useEffect(() => {
    if (!autoplay || images.length <= 1) return;
    const id = setInterval(() => go((active + 1) % images.length), 5000);
    return () => clearInterval(id);
  }, [active, autoplay, images.length, go]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.children[active] as HTMLElement | undefined;
    if (!slide) return;
    // Scroll only the carousel's own horizontal track, never the page. Using
    // slide.scrollIntoView() would move the whole viewport back to the carousel
    // (e.g. on autoplay) while the reader is elsewhere on the page.
    const left = slide.offsetLeft - track.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
    track.scrollTo({ left, behavior: "smooth" });
  }, [active]);

  if (images.length === 0 && !heading && !intro) return null;

  return (
    <section className={styles.textGroup.section} aria-roledescription="carousel">
      {(heading || intro) && (
        <div className="mb-[calc(var(--space-unit)*1.5)] max-w-3xl">
          {heading && (
            <h2
              className="font-bold text-[var(--color-text)]"
              style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h2)", lineHeight: "var(--line-heading)" }}
            >
              {heading}
            </h2>
          )}
          {intro && <p className="mt-2 text-[var(--color-secondary)]">{intro}</p>}
        </div>
      )}
      <div className="relative">
        <div
          ref={trackRef}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2"
          role="list"
          aria-live="polite"
          aria-atomic="false"
        >
          {images.map((image, index) =>
            image.url ? (
              <figure
                key={index}
                role="listitem"
                aria-label={`Slide ${index + 1} of ${images.length}`}
                aria-hidden={index !== active}
                className="w-full shrink-0 snap-center md:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)]"
              >
                <button
                  type="button"
                  onClick={() => setLightboxIndex(lightboxImages.findIndex((candidate) => candidate === image))}
                  className="relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden rounded-[var(--border-radius)] bg-slate-100"
                  aria-label={`Open ${image.altText || image.caption || `gallery image ${index + 1}`} full size`}
                >
                  <Image
                    src={image.url}
                    alt={image.altText || ""}
                    fill
                    className="object-contain object-top transition hover:opacity-90"
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                </button>
                {image.caption && (
                  <figcaption className="mt-2 text-center text-sm text-[var(--color-secondary)]">
                    {image.caption}
                  </figcaption>
                )}
              </figure>
            ) : null,
          )}
        </div>
        {images.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => go(active - 1)}
              aria-label="Previous slide"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden />
            </button>
            <div className="flex gap-1" role="tablist" aria-label="Carousel slides">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  role="tab"
                  aria-selected={index === active}
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => go(index)}
                  className={`h-2.5 w-2.5 rounded-full ${
                    index === active ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => go(active + 1)}
              aria-label="Next slide"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
            >
              <ChevronRight className="h-5 w-5" aria-hidden />
            </button>
          </div>
        )}
      </div>
      <GalleryLightbox
        images={lightboxImages}
        activeIndex={lightboxIndex}
        onChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </section>
  );
}
